import { useHistory, useRouteMatch } from "react-router-dom";
import React, { ReactNode } from "react";
import {
  useBulkCollaboratorBatch,
  useBulkCollaboratorBatchSummary
} from "../../legacyBulkImport/query/bulkCollaborator/queries";
import { WSQueries } from "../../../../query/WSQuery";
import {
  WSButton,
  WSIconList,
  WSMessageBox,
  WSText
} from "@wingspanhq/fe-component-library";
import { useMutationUpdateBulkCollaboratorBatch } from "../../legacyBulkImport/query/bulkCollaborator/mutations";
import { BulkStatus } from "@wingspanhq/payments/dist/interfaces";
import pluralize from "pluralize";
import { SetupRouteComponentProps } from "../../../../shared/components/FlowSetup";
import { PayerSetupLayout } from "../../components/PayerSetupLayout";

export const RouteRecipientsImportPreview: React.FC<
  SetupRouteComponentProps & {
    basePath?: string;
    Layout?: React.FC<{
      title: string;
      children: ReactNode;
      onBack?: () => void;
    }>;
  }
> = ({
  onNext,
  Layout,
  basePath = "/member/1099-filing/set-up/recipients-import"
}) => {
  const history = useHistory();
  const match = useRouteMatch<{ bulkBatchId: string }>();
  const { bulkBatchId } = match.params;

  const qCollaboratorBulkBatch = useBulkCollaboratorBatch(bulkBatchId);
  const qCollaboratorBatchSummary =
    useBulkCollaboratorBatchSummary(bulkBatchId);

  const [updateBulkCollaboratorBatch, updateBulkCollaboratorBatchMeta] =
    useMutationUpdateBulkCollaboratorBatch();

  const startImport = async () => {
    await updateBulkCollaboratorBatch(
      {
        id: bulkBatchId,
        data: {
          status: BulkStatus.Pending
        }
      },
      {
        onSuccess: () => {
          onNext({
            bulkBatchId
          });
        }
      }
    );
  };

  const LayoutComponent = Layout || PayerSetupLayout;

  return (
    <LayoutComponent title="Add recipients">
      <WSQueries
        queries={{
          qCollaboratorBulkBatch,
          qCollaboratorBatchSummary
        }}
      >
        {({
          qCollaboratorBulkBatchData: collaboratorBulkBatch,
          qCollaboratorBatchSummaryData: collaboratorBatchSummary
        }) => {
          const isBatchProcessed = [
            BulkStatus.Complete,
            BulkStatus.Failed
          ].includes(collaboratorBulkBatch.status);
          return (
            <>
              <WSText.Heading4 mb="XL">
                Batch upload recipients with a spreadsheet
              </WSText.Heading4>
              <WSText weight="medium" mb="XL">
                Import preview
              </WSText>

              <WSIconList
                mb="2XL"
                items={[
                  {
                    description: `${pluralize(
                      "recipient",
                      collaboratorBatchSummary.collaboratorsCount,
                      true
                    )} will be imported.`,
                    icon: {
                      name: "user",
                      color: "blue500",
                      size: "M"
                    }
                  }
                ]}
              />

              {isBatchProcessed ? (
                <WSMessageBox.Regular mb="XL">
                  Recipients have already been imported for this batch.{" "}
                  <WSButton.Link
                    onClick={() => {
                      history.push(basePath + `/${bulkBatchId}/upload`);
                    }}
                  >
                    See status here.
                  </WSButton.Link>
                </WSMessageBox.Regular>
              ) : null}
              {collaboratorBulkBatch.status === BulkStatus.Processing ? (
                <WSMessageBox.Regular mb="XL">
                  Recipients are being imported. This may take a few minutes.{" "}
                  <WSButton.Link
                    onClick={() => {
                      history.push(basePath + `/${bulkBatchId}/upload`);
                    }}
                  >
                    See progress here.
                  </WSButton.Link>
                </WSMessageBox.Regular>
              ) : null}
              <WSButton
                onClick={startImport}
                fullWidth
                name="recipientsBatchImport"
                loading={updateBulkCollaboratorBatchMeta.isLoading}
                disabled={isBatchProcessed}
              >
                Import recipients
              </WSButton>
            </>
          );
        }}
      </WSQueries>
    </LayoutComponent>
  );
};
